import { ChainId, NATIVE_TOKEN_ADDRESS } from '@src/configs/chainInfo'
import { FiatCurrency } from '@src/types/fiat'
import { Address, IToken } from '@src/types/token'
import { createContextLogger } from '@src/utils'

const logger = createContextLogger('fiat/config')

// Define all required environment variables
const requiredEnvVars = [
    'REACT_APP_COINBASE_PROJECT_ID',
    'REACT_APP_COINBASE_PUBLIC_API_KEY',
    'REACT_APP_COINBASE_API_HOST',
    'REACT_APP_COINBASE_SECRET_ORG_PREFIX',
    'REACT_APP_COINBASE_SECRET_ECDSA',
    'REACT_APP_COINBASE_SECRET_EDDSA',
]

// Validate environment variables
const validateCoinbaseEnvVars = () => {
    const missingVars = []
    const malformedVars = []

    // Check for missing variables
    for (const varName of requiredEnvVars) {
        if (!process.env[varName]) {
            missingVars.push(varName)
        }
    }

    // Check for JSON-parsable variables
    if (process.env.REACT_APP_COINBASE_SECRET_ECDSA && isMalformedJson(process.env.REACT_APP_COINBASE_SECRET_ECDSA)) {
        malformedVars.push('REACT_APP_COINBASE_SECRET_ECDSA')
    }

    if (process.env.REACT_APP_COINBASE_SECRET_EDDSA && isMalformedJson(process.env.REACT_APP_COINBASE_SECRET_EDDSA)) {
        malformedVars.push('REACT_APP_COINBASE_SECRET_EDDSA')
    }

    // Log results
    if (missingVars.length === 0 && malformedVars.length === 0) {
        logger.success('Coinbase env variables correctly set.')
    } else {
        let errorMsg = 'Coinbase configuration issues found: '

        if (missingVars.length > 0) {
            errorMsg += `Missing variables: ${missingVars.join(', ')}. `
        }

        if (malformedVars.length > 0) {
            errorMsg += `Malformed JSON in: ${malformedVars.join(', ')}.`
        }

        logger.error(errorMsg)
    }
}

// Helper function to check if a string is valid JSON
const isMalformedJson = (jsonString: string): boolean => {
    try {
        JSON.parse(jsonString)
        return false // Not malformed
    } catch (e) {
        return true // Malformed
    }
}

// Run validation
validateCoinbaseEnvVars()

// Export the environment variables
export const COINBASE_PROJECT_ID = process.env.REACT_APP_COINBASE_PROJECT_ID
export const COINBASE_PUBLIC_API_KEY = process.env.REACT_APP_COINBASE_PUBLIC_API_KEY
export const COINBASE_API_HOST = process.env.REACT_APP_COINBASE_API_HOST
export const COINBASE_SECRET_ORG_PREFIX = process.env.REACT_APP_COINBASE_SECRET_ORG_PREFIX

// Parse JSON variables with error handling
export const COINBASE_SECRET_ECDSA = (() => {
    try {
        return JSON.parse(process.env.REACT_APP_COINBASE_SECRET_ECDSA || '')
    } catch (e) {
        return ''
    }
})()

export const COINBASE_SECRET_EDDSA = (() => {
    try {
        return JSON.parse(process.env.REACT_APP_COINBASE_SECRET_EDDSA || '')
    } catch (e) {
        return ''
    }
})()

export const supportedFiatCurrencies: FiatCurrency[] = [
    {
        name: 'US Dollar',
        symbol: 'USD',
        logoURI: '/assets/fiat/FiatCurrency_USD.png',
    },
    {
        name: 'Euro',
        symbol: 'EUR',
        logoURI: '/assets/fiat/FiatCurrency_EUR.png',
    },
    {
        name: 'British Pound',
        symbol: 'GBP',
        logoURI: '/assets/fiat/FiatCurrency_GBP.png',
    },
    {
        name: 'Swiss Franc',
        symbol: 'CHF',
        logoURI: '/assets/fiat/FiatCurrency_CHF.png',
    },
    {
        name: 'Hungarian Forint',
        symbol: 'HUF',
        logoURI: '/assets/fiat/FiatCurrency_HUF.png',
    },
    {
        name: 'Swedish Krona',
        symbol: 'SEK',
        logoURI: '/assets/fiat/FiatCurrency_SEK.png',
    },
    {
        name: 'Norwegian Krone',
        symbol: 'NOK',
        logoURI: '/assets/fiat/FiatCurrency_NOK.png',
    },
    {
        name: 'United Arab Emirates Dirham',
        symbol: 'AED',
        logoURI: '/assets/fiat/FiatCurrency_AED.png',
    },
    {
        name: 'Australian Dollar',
        symbol: 'AUD',
        logoURI: '/assets/fiat/FiatCurrency_AUD.png',
    },
    {
        name: 'Canadian Dollar',
        symbol: 'CAD',
        logoURI: '/assets/fiat/FiatCurrency_CAD.png',
    },
    {
        name: 'Indian Rupee',
        symbol: 'INR',
        logoURI: '/assets/fiat/FiatCurrency_INR.png',
    },
    {
        name: 'Czech Koruna',
        symbol: 'CZK',
        logoURI: '/assets/fiat/FiatCurrency_CZK.png',
    },
    {
        name: 'Romanian Leu',
        symbol: 'RON',
        logoURI: '/assets/fiat/FiatCurrency_RON.png',
    },
]

export const supportedTokens: IToken[] = [
    {
        address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48' as Address,
        chainId: ChainId.Ethereum,
        decimals: 6,
        logoURI: 'https://coin-images.coingecko.com/coins/images/6319/large/usdc.png?1696506694',
        name: 'USDC',
        symbol: 'USDC',
        native: false,
    },
    {
        address: NATIVE_TOKEN_ADDRESS[ChainId.Ethereum] as Address,
        chainId: ChainId.Ethereum,
        decimals: 18,
        logoURI: `/assets/token/eth.png`,
        name: 'ETH',
        symbol: 'ETH',
        native: true,
    },
    // We will add Solana support later
    // {
    //     address: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v' as Address,
    //     chainId: ChainId.Solana,
    //     decimals: 6,
    //     logoURI: 'https://coin-images.coingecko.com/coins/images/6319/large/usdc.png?1696506694',
    //     name: 'USDC',
    //     native: false,
    //     symbol: 'USDC',
    // },
    {
        symbol: 'USDC',
        logoURI: 'https://coin-images.coingecko.com/coins/images/6319/large/usdc.png?1696506694',
        name: 'USDC',
        chainId: ChainId.Base,
        address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913' as Address,
        decimals: 6,
        native: false,
    },
    {
        address: NATIVE_TOKEN_ADDRESS[ChainId.Base] as Address,
        chainId: ChainId.Base,
        decimals: 18,
        logoURI: `/assets/token/eth.png`,
        name: 'ETH',
        symbol: 'ETH',
        native: true,
    },
    {
        symbol: 'USDC',
        logoURI: 'https://coin-images.coingecko.com/coins/images/6319/large/usdc.png?1696506694',
        name: 'USDC',
        chainId: ChainId.Optimism,
        address: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85' as Address,
        decimals: 6,
        native: false,
    },
    {
        address: NATIVE_TOKEN_ADDRESS[ChainId.Optimism] as Address,
        chainId: ChainId.Optimism,
        decimals: 18,
        logoURI: `/assets/token/eth.png`,
        name: 'ETH',
        symbol: 'ETH',
        native: true,
    },
]

export const DEFAULT_ONRAMP_TOKEN: IToken = {
    symbol: 'USDC',
    logoURI: 'https://coin-images.coingecko.com/coins/images/6319/large/usdc.png?1696506694',
    name: 'USDC',
    chainId: ChainId.Base,
    address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913' as Address,
    decimals: 6,
    native: false,
} as IToken

export const DEFAULT_ONRAMP_FIAT_CURRENCY: FiatCurrency = {
    name: 'US Dollar',
    symbol: 'USD',
    logoURI: '/assets/fiat/FiatCurrency_USD.png',
} as FiatCurrency
